import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

export interface BackingCodingCycleData {
  cycleNum: string;
  product: string;
  executiveName: string;
  sku: string;
  machineProof: string;
  remarks: string;
}

export interface OfflineFileData {
  id: string;
  cycleNum: number;
  productName?: string;
  fileData: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  timestamp: number;
  qualityTourId: string;
  previewUrl: string;
}

interface OfflineSavedData {
  cycleNo: number;
  records: BackingCodingCycleData[];
  timestamp: number;
}

interface BackingCodingState {
  fetchedCycles: BackingCodingCycleData[];
  offlineSavedData: OfflineSavedData[];
  offlineFiles: OfflineFileData[];
  isLoading: boolean;
  isSyncing: boolean;
  error: string | null;
  lastFetchTimestamp: number | null;
}

const initialState: BackingCodingState = {
  fetchedCycles: [],
  offlineSavedData: [],
  offlineFiles: [],
  isLoading: false,
  isSyncing: false,
  error: null,
  lastFetchTimestamp: null,
};

const backingCodingSlice = createSlice({
  name: 'backingCoding',
  initialState,
  reducers: {
    addOfflineData: (state, action: PayloadAction<OfflineSavedData>) => {
      state.offlineSavedData.push(action.payload);
    },
    clearOfflineData: (state) => {
      state.offlineFiles.forEach(file => {
        if (file.previewUrl) URL.revokeObjectURL(file.previewUrl);
      });
      state.offlineSavedData = [];
      state.offlineFiles = [];
    },
    removeOfflineDataItem: (state, action: PayloadAction<number>) => {
      state.offlineSavedData = state.offlineSavedData.filter(
        item => item.cycleNo !== action.payload
      );
    },
    setFetchedCycles: (state, action: PayloadAction<BackingCodingCycleData[]>) => {
      state.fetchedCycles = action.payload;
      state.lastFetchTimestamp = Date.now();
    },
    clearError: (state) => {
      state.error = null;
    },
    resetState: (state) => {
      state.offlineFiles.forEach(file => {
        if (file.previewUrl) URL.revokeObjectURL(file.previewUrl);
      });
      state.fetchedCycles = [];
      state.offlineSavedData = [];
      state.offlineFiles = [];
      state.isLoading = false;
      state.isSyncing = false;
      state.error = null;
      state.lastFetchTimestamp = null;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setSyncing: (state, action: PayloadAction<boolean>) => {
      state.isSyncing = action.payload;
    },
    addOfflineFile: (state, action: PayloadAction<OfflineFileData>) => {
      state.offlineFiles.push(action.payload);
    },
    removeFile: (state, action: PayloadAction<string>) => {
      const fileIndex = state.offlineFiles.findIndex(file => file.id === action.payload);
      if (fileIndex !== -1) {
        const file = state.offlineFiles[fileIndex];
        if (file.previewUrl) URL.revokeObjectURL(file.previewUrl);
        state.offlineFiles.splice(fileIndex, 1);
      }
    },
    clearOfflineFiles: (state) => {
      state.offlineFiles.forEach(file => {
        if (file.previewUrl) URL.revokeObjectURL(file.previewUrl);
      });
      state.offlineFiles = [];
    },
  },
});

export const {
  addOfflineData,
  clearOfflineData,
  removeOfflineDataItem,
  setFetchedCycles,
  clearError,
  resetState,
  setLoading,
  setSyncing,
  addOfflineFile,
  removeFile,
  clearOfflineFiles,
} = backingCodingSlice.actions;

export const selectBackingCodingState = (state: { backingCoding: BackingCodingState }) => state.backingCoding;
export const selectOfflineDataCount = (state: { backingCoding: BackingCodingState }) => state.backingCoding.offlineSavedData.length;
export const selectOfflineFilesCount = (state: { backingCoding: BackingCodingState }) => state.backingCoding.offlineFiles.length;
export const selectFetchedCycles = (state: { backingCoding: BackingCodingState }) => state.backingCoding.fetchedCycles;

export default backingCodingSlice.reducer;
